import * as React from 'react';
import { MdBusiness, MdClose, MdLocationOn, MdPeople, MdSearch, MdVisibility } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import DataGrid from '../../../Components/Table/DataGrid';
import { APPRoutes } from '../../../Utils/Route';
import { useAuth } from '../../../Context/AuthContext';
import { PermissionCodes, permissionHas, resolveUserPermissionCodes, isSuperAdminUser } from '../../../Utils/Permissions';
import { toast } from 'react-toastify';
import { getAllPatientAnalytics } from '../../../Services/AnalyticsService';
import PageTitle from '../../../Components/Common/PageTitle';

const formatDateDisplay = (d) => {
  if (!d) return '';
  if (d instanceof Date) {
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  }
  if (typeof d === 'string') return d;
  return '';
};

const getOrganizationName = (row) =>
  row.OrganizationName || row.organization_name || row.Organization?.Name || row.Organization?.OrganizationName || '';

const getClientName = (row) =>
  row.ClientName || row.account_name || row.client_name || row.Client?.Name || row.Client?.ClientName || '';

const getFacilityName = (row) =>
  row.FacilityName || row.facility_name || row.facilityName || '';

const getProviderCount = (row) => {
  if (typeof row.providers_count === 'number') return row.providers_count;
  if (typeof row.provider_count === 'number') return row.provider_count;
  if (typeof row.providerCount === 'number') return row.providerCount;
  if (Array.isArray(row.providers)) return row.providers.length;
  return 0;
};

export default function AnalyticsPatientsList() {
  const navigate = useNavigate();
  const { token, permissions, decodedToken } = useAuth();
  const userPermissionCodes = React.useMemo(
    () => resolveUserPermissionCodes({ permissions, decodedToken }),
    [permissions, decodedToken],
  );

  const canList = permissionHas(userPermissionCodes, PermissionCodes.ANALYTICS_PATIENTS_LIST, decodedToken);
  const canViewDetails = permissionHas(userPermissionCodes, PermissionCodes.ANALYTICS_PATIENTS_VIEW, decodedToken);

  const isSuperAdmin = isSuperAdminUser(decodedToken);
  const [rows, setRows] = React.useState([]);
  const [search, setSearch] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [summary, setSummary] = React.useState({
    total_organizations: 0,
    total_facilities: 0,
  });

  const loadPatientAnalytics = React.useCallback(async () => {
    if (!token) return;
    try {
      setLoading(true);
      const response = await getAllPatientAnalytics(token);
      const organizations = response?.data?.organizations || [];
      const summaryData = response?.data?.summary || {};
      const rowsData = Array.isArray(organizations) ? organizations : [];
      const flattenedRows = rowsData.flatMap((item) => {
        const facilityNames = item.facility_names || item.FacilityNames || [];
        if (Array.isArray(facilityNames) && facilityNames.length > 0) {
          return facilityNames.map((facility_name) => ({
            ...item,
            facility_name,
          }));
        }
        return [{ ...item, facility_name: item.facility_name || item.FacilityName || '' }];
      });
      setRows(flattenedRows);
      setSummary({
        total_organizations: Number(summaryData.total_organizations) || 0,
        total_facilities: Number(summaryData.total_facilities) || 0,
      });
    } catch (error) {
      console.error('Error loading analytics patients:', error);
      setRows([]);
      setSummary({
        total_organizations: 0,
        total_facilities: 0,
      });
      toast.error('Unable to load patient analytics.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  React.useEffect(() => {
    loadPatientAnalytics();
  }, [loadPatientAnalytics]);

  const filteredRows = React.useMemo(() => {
    const term = search.trim().toLowerCase();
    return rows.filter((row) => {
      const facility = getFacilityName(row).toLowerCase();
      const organization = getOrganizationName(row).toLowerCase();
      const client = getClientName(row).toLowerCase();
      return (
        !term ||
        facility.includes(term) ||
        organization.includes(term) ||
        client.includes(term)
      );
    });
  }, [rows, search]);
  const columns = React.useMemo(() => {
    const cols = [
      {
        id: 'facility_name',
        label: 'Facility',
        minWidth: 240,
        format: (_value, row) => getFacilityName(row) || <span className="text-secondary">—</span>,
      },
      {
        id: 'OrganizationName',
        label: 'Organization',
        minWidth: 220,
        format: (_value, row) => (
          <span className="text-dark">
            {getOrganizationName(row) || '—'}
          </span>
        ),
      },
    ];

    if (isSuperAdmin) {
      cols.push({
        id: 'ClientName',
        label: 'Client',
        minWidth: 180,
        format: (_value, row) => getClientName(row) || <span className="text-secondary">—</span>,
      });
    }

    cols.push(
      {
        id: 'created_by_name',
        label: 'Created By',
        minWidth: 180,
        format: (_value, row) => row.created_by_name || row.CreatedByName || row.createdByName || <span className="text-secondary">—</span>,
      },
    );

    if (canViewDetails) {
      cols.push({
        id: 'actions',
        label: 'Actions',
        minWidth: 120,
        align: 'center',
        format: (_value, row) => (
          <div className="d-flex align-items-center justify-content-center gap-2">
            <button
              type="button"
              title="View"
              className="btn btn-sm p-1 lh-1 text-primary border-0 rounded-2"
              style={{ backgroundColor: 'var(--hc-status-info-bg)' }}
              onClick={() => {
                const orgId = row.organization_id ?? row.OrganizationId ?? row.organization?.id ?? row.Organization?.Id ?? '';
                const facilityRaw = getFacilityName(row) || '';
                const facility = encodeURIComponent(facilityRaw);
                // default to last 30 days
                const end = new Date();
                const start = new Date();
                start.setDate(end.getDate() - 30);
                const dateRange = { start_date: formatDateDisplay(start), end_date: formatDateDisplay(end) };
                navigate(APPRoutes.ANALYTICS_PATIENT_VIEW.replace(':organization_id', orgId).replace(':facility_name', facility), {
                  state: { patientAnalyticsRow: row, date_range: dateRange, facility_name: facilityRaw },
                });
              }}
            >
              <MdVisibility size={16} />
            </button>
          </div>
        ),
      });
    }

    return cols;
  }, [navigate, canViewDetails, isSuperAdmin]);

  const stats = React.useMemo(
    () => [
      {
        label: 'Total Organizations',
        value: summary.total_organizations,
        icon: <MdBusiness size={22} color="var(--hc-icon-blue-text)" />,
        iconBg: 'var(--hc-icon-blue-bg)',
        accentClass: 'border-primary',
        valueClass: 'text-primary',
      },
      {
        label: 'Total Facilities',
        value: summary.total_facilities,
        icon: <MdLocationOn size={22} color="var(--hc-icon-amber-text)" />,
        iconBg: 'var(--hc-icon-amber-bg)',
        accentClass: 'border-warning',
        valueClass: 'text-warning',
      },
    ],
    [summary],
  );

  return (
    <div>
      <PageTitle title="Patients - Aegis 360"/>
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start gap-3 mb-4">
        <div>
          <h4 className="fw-bold mb-1 hc-text-primary fs-3">Analytics Patients</h4>
          <p className="text-secondary mb-0 small">
            View patient analytics grouped by provider organization.
          </p>
        </div>
      </div>

      <div className="row g-3 mb-3">
        {stats.map((stat) => (
          <div key={stat.label} className="col-12 col-sm-6 col-lg-4 col-xl">
            <div className="position-relative overflow-hidden bg-white p-4 rounded-4 shadow p-3 d-flex align-items-center gap-3 h-100 hc-list-kpi-card">
              <div className={`position-absolute start-0 top-0 h-100 border-start border-5 ${stat.accentClass}`} />
              <div
                className="d-inline-flex align-items-center justify-content-center rounded-3"
                style={{ width: 44, height: 44, backgroundColor: stat.iconBg }}
              >
                {stat.icon}
              </div>
              <div className="d-flex flex-column fw-bold lh-sm">
                <span className="text-dark" style={{ fontSize: '0.85rem' }}>
                  {stat.label}
                </span>
                <span className={`fw-bold ${stat.valueClass} fs-5`}>
                  {stat.value}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-3 shadow-lg p-3 p-md-4">
        <div className="row g-2 mb-3 align-items-center">
          <div className="col-12">
            <div className="position-relative">
              <span
                className="position-absolute top-50 translate-middle-y d-inline-flex align-items-center justify-content-center text-secondary pe-none"
                style={{ left: '12px' }}
              >
                <MdSearch size={18} />
              </span>
              <input
                type="text"
                className="form-control form-control-sm hc-input w-100"
                placeholder="Search by facility, organization or client..."
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                style={{ paddingLeft: '36px', paddingRight: search ? '36px' : '12px', height: '40px' }}
              />
              {search ? (
                <button
                  type="button"
                  aria-label="Clear search"
                  onClick={() => setSearch('')}
                  className="btn btn-link bg-danger p-0 position-absolute top-50 translate-middle-y text-light rounded-circle"
                  style={{ right: '8px', lineHeight: 0 }}
                >
                  <MdClose size={18} />
                </button>
              ) : null}
            </div>
          </div>
        </div>

        {loading ? (
          <div>
            {[...Array(8)].map((_, index) => (
              <div key={index} className="d-flex gap-3 align-items-center mb-3">
                  <div className="skeleton position-relative overflow-hidden rounded-2 w-25" style={{ height: 16 }} />
                  <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 16, width: '20%' }} />
                  <div className="skeleton position-relative overflow-hidden rounded-2 w-25" style={{ height: 16 }} />
                  <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 16, width: '20%' }} />
                  <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 16, width: '10%' }} />
                </div>
            ))}
          </div>
        ) : (
          <DataGrid
            columns={columns}
            rows={filteredRows}
            defaultRowsPerPage={10}
            stickyHeader
            getRowId={(row) =>
              row.Id ??
              row.id ??
              `${row.organization_id || row.OrganizationId || row.Organization?.Id || row.Organization?.organization_id}-${getFacilityName(row)}-${getClientName(row)}`
            }
          />
        )}
      </div>
    </div>
  );
}
